<template>
  <RrForm class="ren-props-inspector" layout="vertical" size="small">
    <template v-for="field in fields" :key="field.code">
      <RrFormItem v-if="field.options?.length">
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrSelect
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :value="String(propInput(field.code, field.type))"
          :options="toSelectOptions(field.options)"
          @update:value="(val) => setOption(field, String(val))"
        />
      </RrFormItem>
      <div v-else-if="field.type === 'boolean'" class="ren-props-inspector__switch">
        <span>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </span>
        <RrSwitch
          :checked="booleanValue(getValue(field.code))"
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          @update:checked="(val) => emit('change', field.code, Boolean(val))"
        />
      </div>
      <section v-else-if="isBannerOrListItemsField(field)" class="ren-props-inspector__items">
        <header>
          <strong>
            {{ field.label }}（{{ bannerItems(field.code).length }} 项）
            <TemplateLockMark :locked="isLocked(field.code)" />
          </strong>
          <RrButton
            type="primary"
            size="small"
            :disabled="isDisabled(field.code) || bannerItems(field.code).length >= 12"
            @click="addBannerItem(field.code)">
            添加项
          </RrButton>
        </header>
        <article v-for="(item, index) in bannerItems(field.code)" :key="index" class="ren-props-inspector__item">
          <header>
            <span>第 {{ index + 1 }} 项</span>
            <div class="ren-props-inspector__item-actions">
              <RrButton
                size="small"
                type="default"
                :disabled="isDisabled(field.code) || index === 0"
                aria-label="上移"
                @click="moveBannerItem(field.code, index, -1)">
                <ArrowUp :size="12" />
              </RrButton>
              <RrButton
                size="small"
                type="default"
                :disabled="isDisabled(field.code) || index === bannerItems(field.code).length - 1"
                aria-label="下移"
                @click="moveBannerItem(field.code, index, 1)">
                <ArrowDown :size="12" />
              </RrButton>
              <RrButton
                size="small"
                type="primary" danger
                :disabled="isDisabled(field.code) || bannerItems(field.code).length <= 1"
                aria-label="删除"
                @click="removeBannerItem(field.code, index)">
                <Trash2 :size="12" />
              </RrButton>
            </div>
          </header>
          <div class="ren-props-inspector__item-body">
            <WidgetImageUpload
              :model-value="String(item.imageUrl || item.image || '')"
              :disabled="isDisabled(field.code)"
              @update:model-value="(url) => setBannerItem(field.code, index, 'imageUrl', url)"
            />
            <div class="ren-props-inspector__item-fields">
              <RrInput
                placeholder="请输入标题"
                :disabled="isDisabled(field.code)"
                :value="String(item.title ?? item.label ?? '')"
                @update:value="(val) => setBannerItem(field.code, index, 'title', String(val))"
              />
              <RrInput
                placeholder="请输入副标题（选填）"
                :disabled="isDisabled(field.code)"
                :value="String(item.subtitle ?? '')"
                @update:value="(val) => setBannerItem(field.code, index, 'subtitle', String(val))"
              />
              <div class="ren-props-inspector__item-link">
                <span>{{ item.link || '未配置跳转' }}</span>
                <RrButton
                  size="small"
                  type="default"
                  :disabled="readonly || !canEditProp('navigation')"
                  @click="emit('pick-item-link', index)">
                  选择页面
                </RrButton>
              </div>
            </div>
          </div>
        </article>
        <details class="ren-decoration-json">
          <summary>高级：JSON 代码</summary>
          <RrInput
            type="textarea"
            :disabled="isDisabled(field.code)"
            :value="propText(field.code)"
            @update:value="(val) => setText(field.code, field.type, String(val))"
          />
        </details>
      </section>
      <RrFormItem v-else-if="isSingleImageField(field)">
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <div class="ren-props-inspector__image">
          <WidgetImageUpload
            :model-value="String(getValue(field.code) ?? '')"
            :disabled="isDisabled(field.code)"
            @update:model-value="(url) => emit('change', field.code, url)"
          />
          <RrInput
            placeholder="请输入图片 URL 或点击左侧上传"
            :disabled="isDisabled(field.code)"
            :value="String(propInput(field.code, 'text') ?? '')"
            @update:value="(val) => setInput(field.code, 'text', String(val))"
          />
        </div>
      </RrFormItem>
      <RrFormItem v-else-if="field.type === 'text' || field.type === 'list'">
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrInput
          type="textarea"
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :value="propText(field.code)"
          @update:value="(val) => setText(field.code, field.type, String(val))"
        />
      </RrFormItem>
      <RrFormItem v-else-if="field.type === 'color'">
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrColorPicker
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :model-value="String(propInput(field.code, field.type) ?? '')"
          @update:model-value="(val) => setInput(field.code, field.type, String(val))"
        />
      </RrFormItem>
      <RrFormItem v-else-if="field.type === 'number'">
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrInputNumber
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :value="Number(propInput(field.code, 'number') || 0)"
          @update:value="(val) => emit('change', field.code, Number(val || 0))"
        />
      </RrFormItem>
      <RrFormItem v-else>
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrInput
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :value="String(propInput(field.code, field.type) ?? '')"
          @update:value="(val) => setInput(field.code, field.type, String(val))"
        />
      </RrFormItem>
    </template>
  </RrForm>
</template>

<script setup lang="ts">
import { ArrowDown, ArrowUp, Trash2 } from '@lucide/vue'
import {
  RrButton,
  RrColorPicker,
  RrForm,
  RrFormItem,
  RrInput,
  RrInputNumber,
  RrSelect,
  RrSwitch,
  type SelectOption,
} from '@xjlv2/ui'
import type { RenDecorationComponent, RenDecorationFieldDefinition } from '../../types'
import TemplateLockMark from '../utils/TemplateLockMark'
import WidgetImageUpload from '../widgets/WidgetImageUpload.vue'

type BannerItem = Record<string, unknown> & {
  title?: string
  subtitle?: string
  imageUrl?: string
  image?: string
  link?: string
  label?: string
}

const props = withDefaults(
  defineProps<{
    component: RenDecorationComponent
    fields?: RenDecorationFieldDefinition[]
    readonly?: boolean
    templateBound?: boolean
    canEditProp?: (code: string) => boolean
    getValue?: (code: string) => unknown
  }>(),
  {
    fields: () => [],
    readonly: false,
    templateBound: false,
    canEditProp: () => true,
  },
)

const emit = defineEmits<{
  change: [code: string, value: unknown]
  'pick-item-link': [index: number]
}>()

function getValue(code: string): unknown {
  return props.getValue ? props.getValue(code) : props.component.props?.[code]
}

function isDisabled(code: string): boolean {
  return props.readonly || !props.canEditProp(code)
}

function isLocked(code: string): boolean {
  return props.templateBound && !props.canEditProp(code)
}

function fieldTitle(code: string): string {
  return isLocked(code) ? '该字段由业务模板锁定' : ''
}

function propInput(code: string, type: string) {
  const value = getValue(code)
  if (type === 'number') return Number(value ?? 0)
  return value == null ? (type === 'color' ? '#FFFFFF' : '') : String(value)
}

function propText(code: string) {
  const value = getValue(code)
  return typeof value === 'string' ? value : JSON.stringify(value ?? [], null, 2)
}

function booleanValue(value: unknown, fallback = false) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true
    if (['false', '0', 'no', 'off'].includes(normalized)) return false
  }
  return value == null ? fallback : Boolean(value)
}

function isBannerOrListItemsField(field: RenDecorationFieldDefinition) {
  if (field.code !== 'items' && field.code !== 'banners' && field.code !== 'images') return false
  const val = getValue(field.code)
  return Array.isArray(val) || (typeof val === 'object' && val !== null)
}

function isSingleImageField(field: RenDecorationFieldDefinition) {
  return field.type === 'image' || field.code === 'imageUrl' || field.code === 'image' || field.code === 'cover'
}

function bannerItems(fieldCode: string): BannerItem[] {
  const raw = getValue(fieldCode)
  return Array.isArray(raw) ? (raw as BannerItem[]) : []
}

function commitBannerItems(fieldCode: string, items: BannerItem[]) {
  emit('change', fieldCode, items)
}

function setBannerItem(fieldCode: string, index: number, key: string, value: unknown) {
  const items = [...bannerItems(fieldCode)]
  if (!items[index]) return
  items[index] = { ...items[index], [key]: value }
  commitBannerItems(fieldCode, items)
}

function addBannerItem(fieldCode: string) {
  const items = [...bannerItems(fieldCode)]
  if (items.length >= 12) return
  items.push({ title: `轮播图 ${items.length + 1}`, subtitle: '', imageUrl: '', link: '' })
  commitBannerItems(fieldCode, items)
}

function removeBannerItem(fieldCode: string, index: number) {
  const items = bannerItems(fieldCode)
  if (items.length <= 1) return
  commitBannerItems(
    fieldCode,
    items.filter((_, i) => i !== index),
  )
}

function moveBannerItem(fieldCode: string, index: number, offset: -1 | 1) {
  const items = [...bannerItems(fieldCode)]
  const target = index + offset
  if (target < 0 || target >= items.length) return
  const [moved] = items.splice(index, 1)
  items.splice(target, 0, moved)
  commitBannerItems(fieldCode, items)
}

function setOption(field: RenDecorationFieldDefinition, value: string) {
  const option = field.options?.find((item) => String(item.value) === value)
  emit('change', field.code, option?.value ?? value)
}

function setInput(code: string, type: string, value: string) {
  emit('change', code, type === 'number' ? Number(value || 0) : value)
}

function setText(code: string, type: string, value: string) {
  if (type !== 'list') {
    emit('change', code, value)
    return
  }
  try {
    emit('change', code, JSON.parse(value))
  } catch {
    emit('change', code, value)
  }
}

function toSelectOptions(options?: Array<{ label?: string; value?: unknown; disabled?: boolean }>): SelectOption[] {
  if (!options) return []
  return options.map((opt) => ({
    label: String(opt.label ?? opt.value ?? ''),
    value: (opt.value as string | number | boolean | null) ?? '',
    disabled: opt.disabled,
  }))
}
</script>

<style scoped>
.ren-props-inspector {
  display: grid;
  gap: var(--rr-space-3);
}

.ren-props-inspector__switch,
.ren-props-inspector__item-link,
.ren-props-inspector__items header,
.ren-props-inspector__item header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--rr-space-2);
}

.ren-props-inspector__items,
.ren-props-inspector__item {
  display: grid;
  gap: var(--rr-space-3);
  padding: var(--rr-space-3);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-control, 6px);
}

.ren-props-inspector__item-actions,
.ren-props-inspector__image {
  display: flex;
  gap: var(--rr-space-2);
}

.ren-props-inspector__item-body,
.ren-props-inspector__item-fields {
  display: grid;
  gap: var(--rr-space-2);
}

.ren-props-inspector :deep(.rr-form-item) {
  margin-bottom: 0;
}

.ren-props-inspector :deep(.rr-form-item__label-text) {
  display: inline-flex;
  align-items: center;
  gap: var(--rr-space-1);
}
</style>
